import { ParseAlgorithmDto, BindingDataDto, PayloadType } from "../types";

type KvTableRaw = {
  id: number;
  key: string;
  parseAlgorithms: { id: number }[];
};

type GroupTableRaw = {
  id: number;
  keyValueTableId: number;
  joinTables: { parseAlgorithmId: number; order: number }[];
};

export const buildUuidIndexMap = (parseTable: ParseAlgorithmDto[]): Map<string, number> =>
  new Map(
    [...parseTable]
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
      .filter(p => p.id)
      .map((p, idx) => [p.id!, idx])
  );

export const hydrateBindingData = (bindingData: BindingDataDto[], uuidIndexMap: Map<string, number>) =>
  bindingData.map(b => ({
    ...b,
    bindingParseData: b.bindingParseData.map(p => ({
      ...p,
      parseId: p.itemId ? uuidIndexMap.get(p.itemId) : undefined,
    })),
  }));

export const normalizeParseTable = (parseTable: ParseAlgorithmDto[]) =>
  parseTable.map(rule => ({
    ...rule,
    id: rule.id?.toString(),
    baseId: rule.parentItemId ?? undefined,
  }));

export const normalizeBindingData = (bindingData: BindingDataDto[]) =>
  bindingData.map(b => ({
    ...b,
    bindingParseData: b.bindingParseData.map(p => {
      const resolvedId =
        p.itemId != null ? p.itemId.toString()
        : p.parseId != null ? p.parseId.toString()
        : null;
      return { itemId: resolvedId === "0" ? null : resolvedId, order: p.order };
    }),
  }));

export const buildUpdatePayload = (
  payloadData: PayloadType[],
  kvTablesRaw: KvTableRaw[],
  groupTablesRaw: GroupTableRaw[],
  parserConfigId: number,
) =>
  payloadData.filter(x => x.dbRecord).map(item => {
    const kvRaw = kvTablesRaw.find(k => k.key === item.key);
    const dbAlgoIds = kvRaw?.parseAlgorithms.map(a => a.id) ?? [];
    const kvGroupTables = groupTablesRaw.filter(g => g.keyValueTableId === kvRaw?.id);

    let newCounter = 0;
    const uuidToId: Record<string, string> = {};

    const parseTable = (item.parseTable ?? []).map((algo, i) => {
      const dbId = dbAlgoIds[i];
      const assignedId = dbId != null ? dbId.toString() : `new-${++newCounter}`;
      if (algo.id) uuidToId[algo.id] = assignedId;
      return { ...algo, id: assignedId };
    });

    const bindingData = (item.bindingData ?? []).map((binding, j) => ({
      ...binding,
      Id: kvGroupTables[j]?.id ?? null,
      bindingParseData: binding.bindingParseData.map(bp => ({
        order: bp.order,
        itemId: bp.itemId != null
          ? (uuidToId[bp.itemId] ?? bp.itemId)
          : bp.parseId != null ? bp.parseId.toString() : null,
      })),
    }));

    return {
      key: item.key,
      isRawData: item.isRawData,
      rawDataTypeId: item.rawDataTypeId ?? 0,
      parserConfigId,
      description: item.description ?? "",
      isDeviceId: item.isDeviceId ?? false,
      isArrayExpand: item.isArrayExpand ?? false,
      parseTable: item.isRawData ? parseTable : [],
      bindingData: item.isRawData ? bindingData : [],
    };
  });
